import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Card,
    CardContent,
    Typography,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    FormHelperText
} from '@mui/material';
import {
    FlightTakeoff,
    Water,
    DirectionsCar,
    PrecisionManufacturing,
    Group,
    Extension
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { createProjectFromTemplate } from 'src/store/slices/projectSlice';
import { ProjectType } from 'src/types/project';
import { useValidation } from 'src/hooks/useValidation';
import { validateProjectName, sanitizeString } from 'src/utils/validation';

interface NewProjectDialogProps {
    open: boolean;
    onClose: () => void;
}

const projectTypeIcons = {
    drone_delivery: <FlightTakeoff />,
    underwater_inspection: <Water />,
    ground_robot: <DirectionsCar />,
    manipulator: <PrecisionManufacturing />,
    multi_robot: <Group />,
    custom: <Extension />
};

const projectTypeDescriptions = {
    drone_delivery: 'Autonomous aerial vehicles for package delivery and logistics',
    underwater_inspection: 'Submersible robots for underwater pipeline and infrastructure inspection',
    ground_robot: 'Mobile ground-based robots for indoor/outdoor navigation and tasks',
    manipulator: 'Robotic arms and manipulation systems for pick-and-place operations',
    multi_robot: 'Coordinated systems with multiple robots working together',
    custom: 'Start from scratch with your own custom project configuration'
};

const NewProjectDialog: React.FC<NewProjectDialogProps> = ({ open, onClose }) => {
    const dispatch = useAppDispatch();
    const { templates } = useAppSelector(state => state.project);
    const [selectedTemplate, setSelectedTemplate] = useState<string>('');
    const [selectedType, setSelectedType] = useState<ProjectType>('drone_delivery');

    // Validation
    const {
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        validateAll,
        reset,
    } = useValidation(
        {
            projectName: '',
            author: '',
        },
        {
            projectName: (value: string) => validateProjectName(value),
            author: (value: string) => {
                if (value && value.trim().length > 100) {
                    return { isValid: false, error: 'Author name must be less than 100 characters' };
                }
                return { isValid: true };
            },
        },
        {
            validateOnChange: true,
            validateOnBlur: true,
        }
    );

    const handleCreate = () => {
        if (!validateAll()) {
            return;
        }

        const sanitizedProjectName = sanitizeString(values.projectName);
        const sanitizedAuthor = sanitizeString(values.author);

        if (selectedTemplate) {
            // Create from template
            dispatch(createProjectFromTemplate({
                templateId: selectedTemplate,
                projectName: sanitizedProjectName,
                author: sanitizedAuthor || 'Unknown'
            }));
        } else {
            // Create custom project
            // This would create a basic project structure
            // For now, we'll just close the dialog
        }

        handleClose();
    };

    const handleClose = () => {
        setSelectedTemplate('');
        reset();
        setSelectedType('drone_delivery');
        onClose();
    };

    const handleTemplateSelect = (templateId: string) => {
        setSelectedTemplate(templateId);
        const template = templates.find(t => t.id === templateId);
        if (template) {
            setSelectedType(template.type);
            if (!values.projectName) {
                handleChange('projectName')(template.name);
            }
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { minHeight: '600px' }
            }}
        >
            <DialogTitle>
                Create New Robotics Project
            </DialogTitle>

            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        label="Project Name"
                        value={values.projectName}
                        onChange={(e) => handleChange('projectName')(e.target.value)}
                        onBlur={handleBlur('projectName')}
                        placeholder="Enter your project name"
                        margin="normal"
                        required
                        error={touched.projectName && !!errors.projectName}
                        helperText={touched.projectName && errors.projectName}
                    />

                    <TextField
                        fullWidth
                        label="Author"
                        value={values.author}
                        onChange={(e) => handleChange('author')(e.target.value)}
                        onBlur={handleBlur('author')}
                        placeholder="Your name"
                        margin="normal"
                        error={touched.author && !!errors.author}
                        helperText={touched.author && errors.author}
                    />
                </Box>

                <Typography variant="h6" gutterBottom>
                    Choose Project Template
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Select a template to get started quickly, or choose Custom to build from scratch.
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                    {/* Template Cards */}
                    {templates.map((template) => (
                        <Box key={template.id}>
                            <Card
                                sx={{
                                    cursor: 'pointer',
                                    border: selectedTemplate === template.id ? 2 : 1,
                                    borderColor: selectedTemplate === template.id ? 'primary.main' : 'divider',
                                    '&:hover': {
                                        borderColor: 'primary.light',
                                        transform: 'translateY(-2px)',
                                        transition: 'all 0.2s'
                                    }
                                }}
                                onClick={() => handleTemplateSelect(template.id)}
                            >
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Box sx={{ mr: 2, color: 'primary.main' }}>
                                            {projectTypeIcons[template.type]}
                                        </Box>
                                        <Typography variant="h6" component="h3">
                                            {template.name}
                                        </Typography>
                                    </Box>

                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {template.description}
                                    </Typography>

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                                        <Chip
                                            label={template.type.replace('_', ' ').toUpperCase()}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Box>

                                    {template.metadata?.objectives && (
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Key Features:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                                {template.metadata.objectives.slice(0, 2).join(', ')}
                                                {template.metadata.objectives.length > 2 && '...'}
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Box>
                    ))}

                    {/* Custom Project Card */}
                    <Box>
                        <Card
                            sx={{
                                cursor: 'pointer',
                                border: selectedTemplate === 'custom' ? 2 : 1,
                                borderColor: selectedTemplate === 'custom' ? 'primary.main' : 'divider',
                                '&:hover': {
                                    borderColor: 'primary.light',
                                    transform: 'translateY(-2px)',
                                    transition: 'all 0.2s'
                                }
                            }}
                            onClick={() => {
                                setSelectedTemplate('custom');
                                setSelectedType('custom');
                            }}
                        >
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Box sx={{ mr: 2, color: 'primary.main' }}>
                                        <Extension />
                                    </Box>
                                    <Typography variant="h6" component="h3">
                                        Custom Project
                                    </Typography>
                                </Box>

                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Start from scratch with your own configuration and requirements.
                                </Typography>

                                <Chip
                                    label="CUSTOM"
                                    size="small"
                                    variant="outlined"
                                />
                            </CardContent>
                        </Card>
                    </Box>
                </Box>

                {selectedTemplate === 'custom' && (
                    <Box sx={{ mt: 3 }}>
                        <FormControl fullWidth>
                            <InputLabel>Project Type</InputLabel>
                            <Select
                                value={selectedType}
                                label="Project Type"
                                onChange={(e) => setSelectedType(e.target.value as ProjectType)}
                            >
                                {Object.entries(projectTypeDescriptions).map(([type, description]) => (
                                    <MenuItem key={type} value={type}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Box sx={{ mr: 2 }}>
                                                {projectTypeIcons[type as ProjectType]}
                                            </Box>
                                            <Box>
                                                <Typography variant="body2">
                                                    {type.replace('_', ' ').toUpperCase()}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {description}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={handleClose}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleCreate}
                    disabled={!values.projectName.trim() || !selectedTemplate || !!errors.projectName}
                >
                    Create Project
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NewProjectDialog;